import { useState, useEffect } from "react";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { Utility } from "../../core/Utility";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { Product, ProductOverview, ProductType } from "../../models/Product";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { getAllProducts } from "../../services/ProductService";
import { getSeedStockReport } from "../../services/FinishingCenterService";
import { useApplicationContext } from "../../application/ApplicationContext";
import "./NaturalReproAmountReportPage.scss";

export const NaturalReproAmountReportPage = () => {
  const { t } = useTranslation();
  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();

  const [productsApi, setProductsApi] = useState<Product[]>([]);
  const [repromaterialOverview, setRepromaterialOverview] = useState<
    ProductOverview[]
  >([]);

  const [processing, setProcessing] = useState(false);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const yearStart = new Date(businessYear, 0, 1, 0, 0, 1);
        const yearEnd = new Date(businessYear, 11, 31, 23, 59, 59);

        const pResp: AxiosResponse = await getAllProducts();
        ensureSuccess(pResp);
        const pr: Product[] = pResp?.data?.result ?? [];
        setProductsApi(pr);

        const resp: AxiosResponse = await getSeedStockReport(
          yearStart,
          yearEnd,
        );
        const docs = resp.data?.result ?? [];
        formatRepromaterialOverviewData(docs, pr);
      } catch (error) {
        console.log(error as AxiosError);
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, [businessYear]);

  const formatRepromaterialOverviewData = (docs: any[], pr: Product[]) => {
    const overviewMap = new Map<string, ProductOverview>();

    for (const doc of docs) {
      if (!doc?.finishedDocument) continue;

      // ulaz +, izlaz -
      const sign = doc.input ? 1 : -1;

      const json = doc.repromaterialProductsJSON ?? "[]";
      let items: any[] = [];
      try {
        items = JSON.parse(json);
      } catch {
        continue;
      }

      for (const item of items) {
        const productId = item.id;
        if (!productId) continue;

        const fullProduct = pr.find((p) => p.id === productId);
        if (!fullProduct) continue;

        const category = (fullProduct as any).category;
        const catCode = category?.code;
        const catName = category?.name;

        if (!(catCode === "SAAT" || catName === "SAAT - Seme")) continue;

        const rawAmount = item.amount ?? 0;
        const amountNum = Number(rawAmount);
        if (!amountNum || isNaN(amountNum)) continue;

        const key = productId;
        const existing = overviewMap.get(key);

        if (existing) {
          existing.amount = (existing.amount ?? 0) + sign * amountNum;
        } else {
          overviewMap.set(key, {
            id: productId,
            code: fullProduct.code ?? item.code ?? "",
            name: fullProduct.name ?? item.name ?? "",
            year: undefined,
            type: ProductType.RawMaterial,
            product: fullProduct as Product,
            mark: fullProduct.measuringUnit,
            amount: sign * amountNum,
          });
        }
      }
    }

    setRepromaterialOverview(Array.from(overviewMap.values()));
  };

  const formatMark = (rowData: any) => {
    return productsApi.find((item) => item.id === rowData.product.id)
      ?.measuringUnit;
  };

  const amountOfReproProduct = (rowData: any) => {
    const amount = Number(rowData.amount ?? 0);
    if (Number.isNaN(amount)) return "0";

    if (!Number.isInteger(amount)) {
      const fixed = Number(amount.toFixed(3));
      if (fixed === 0 || fixed === 0.001 || fixed === -0.001) return "0";
      return fixed.toFixed(3);
    }

    return amount.toFixed(0);
  };

  const exportToExcel = () => {
    const excelRepromaterialObject = repromaterialOverview.map(
      (item: ProductOverview) => {
        const amount = item.amount ?? 0;
        const formattedAmount = Number.isInteger(amount)
          ? amount
          : Math.abs(amount) < 0.001
            ? 0
            : amount;

        return {
          SifraRobe: item?.code ?? "",
          Naziv: item?.name ?? "",
          MJ:
            productsApi.find((oneProduct) => oneProduct.id === item?.product?.id)
              ?.measuringUnit ?? "",
          Stanje: formattedAmount,
        };
      },
    );
    let excelObject: any[] = [];
    const emptyRow = {
      SifraRobe: "",
      Naziv: "",
      MJ: "",
      Stanje: "",
    };
    excelObject = [emptyRow, ...excelRepromaterialObject];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      if (worksheet["!ref"]) {
        const range = xlsx.utils.decode_range(worksheet["!ref"]);
        for (let i = range.s.r + 1; i <= range.e.r; i++) {
          const cellAddress = xlsx.utils.encode_cell({ r: i, c: 3 }); // Kolona D (Kolicina)
          if (
            worksheet[cellAddress] &&
            typeof worksheet[cellAddress].v === "number"
          ) {
            worksheet[cellAddress].t = "n"; // Numerički tip
            // Dinamički format u zavisnosti od toga da li je ceo broj
            if (Number.isInteger(worksheet[cellAddress].v)) {
              worksheet[cellAddress].z = "0"; // Format za cele brojeve
            } else {
              worksheet[cellAddress].z = "0.00"; // Format za decimale
            }
          }
        }
      }
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Stanje_zaliha_proizvedenog_semena_na_dan_${Utility.formatDate(
          new Date(),
        )}`,
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes header-title-ic"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
                marginTop: "0.5rem",
              }}
            ></i>
            <span className="header-title">
              {t("Stanje zaliha proizvedenog semena na skladištu materijala")}
            </span>
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
                marginRight: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
            />
          </div>
        </div>
        <div className="table-body-repro">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable value={repromaterialOverview} showGridlines stripedRows>
              <Column field="code" header={t("Šifra robe")} />
              <Column field="name" header={t("Naziv")} />
              <Column body={amountOfReproProduct} header={t("Stanje")} />
              <Column body={formatMark} header={t("M.J.")} />
            </DataTable>
          )}
        </div>
      </div>
    </PageLayout>
  );
};
